import React, { useState, useEffect, useMemo } from 'react';
import { livpureMockData } from '../../data';

const CategoryStatsMatrix = ({ selectedRegion, selectedCity, dateRange, showLocations, showProducts, showApps }) => {
  // View mode tabs
  const [viewMode, setViewMode] = useState('location');
  
  // Expanded state tracking
  const [expandedItems, setExpandedItems] = useState({});
  
  // Category lists for each view
  const categories = {
    location: ['Customer Service', 'Store Experience', 'Product Quality', 'Price & Value'],
    product: ['Product Quality', 'Product Effectiveness', 'Packaging', 'Side Effects'],
    app: ['App Experience', 'Ease of Use', 'Customer Service']
  };
  
  // Update viewMode based on filters
  useEffect(() => {
    if (showLocations && !showProducts && !showApps) {
      setViewMode('location');
    } else if (!showLocations && showProducts && !showApps) {
      setViewMode('product');
    } else if (!showLocations && !showProducts && showApps) {
      setViewMode('app');
    }
  }, [showLocations, showProducts, showApps]);
  
  // Filter reviews based on selected criteria
  const filteredReviews = useMemo(() => {
    let reviews = [...livpureMockData.reviews];
    
    // Filter by date range
    if (dateRange[0] && dateRange[1]) {
      const startDate = dateRange[0].getTime();
      const endDate = dateRange[1].getTime();
      reviews = reviews.filter(review => {
        const reviewDate = new Date(review.date).getTime();
        return reviewDate >= startDate && reviewDate <= endDate;
      });
    }
    
    // Filter by source type
    if (viewMode === 'location') {
      reviews = reviews.filter(review => review.source === 'Google Reviews');
    } else if (viewMode === 'product') {
      reviews = reviews.filter(review => review.source === 'Amazon');
    } else if (viewMode === 'app') {
      reviews = reviews.filter(review => 
        review.source === 'App Store' || review.source === 'Play Store'
      );
    }
    
    // Filter by region and city
    if (selectedRegion !== 'global') {
      reviews = reviews.filter(review => review.region === selectedRegion);
      
      if (selectedCity) {
        reviews = reviews.filter(review => review.city === selectedCity);
      }
    }
    
    return reviews;
  }, [dateRange, viewMode, selectedRegion, selectedCity]);
  
  // Process data into hierarchical structure
  const hierarchicalData = useMemo(() => {
    // Helper to extract main region
    const getMainRegion = (region) => {
      return region.match(/\d+$/) ? region.replace(/\s+\d+$/, '') : region;
    };
    
    // Create structures for each view type
    const locationData = {};
    const productData = {
      'Water Purifiers': { total: 0, positive: 0, categories: {}, items: {} },
      'Accessories': { total: 0, positive: 0, categories: {}, items: {} },
      'Filter Cartridges': { total: 0, positive: 0, categories: {}, items: {} }
    };
    const appData = {
      'App Store': { total: 0, positive: 0, categories: {}, versions: {} },
      'Play Store': { total: 0, positive: 0, categories: {}, versions: {} }
    };
    
    // Process reviews for location data
    if (viewMode === 'location') {
      filteredReviews.forEach(review => {
        const mainRegion = getMainRegion(review.region);
        const subRegion = review.region;
        const city = review.city;
        
        // Initialize main region if not exists
        if (!locationData[mainRegion]) {
          locationData[mainRegion] = {
            total: 0,
            positive: 0,
            categories: {},
            subregions: {}
          };
        }
        
        // Initialize subregion if not exists
        if (!locationData[mainRegion].subregions[subRegion]) {
          locationData[mainRegion].subregions[subRegion] = {
            total: 0,
            positive: 0,
            categories: {},
            cities: {}
          };
        }
        
        // Initialize city if not exists
        if (!locationData[mainRegion].subregions[subRegion].cities[city]) {
          locationData[mainRegion].subregions[subRegion].cities[city] = {
            total: 0,
            positive: 0,
            categories: {}
          };
        }
        
        // Update review counts
        locationData[mainRegion].total++;
        locationData[mainRegion].subregions[subRegion].total++;
        locationData[mainRegion].subregions[subRegion].cities[city].total++;
        
        if (review.sentiment === 'Positive') {
          locationData[mainRegion].positive++;
          locationData[mainRegion].subregions[subRegion].positive++;
          locationData[mainRegion].subregions[subRegion].cities[city].positive++;
        }
        
        // Process category sentiments
        review.categorySentiments.forEach(cs => {
          // Process for region
          if (!locationData[mainRegion].categories[cs.category]) {
            locationData[mainRegion].categories[cs.category] = { total: 0, positive: 0 };
          }
          locationData[mainRegion].categories[cs.category].total++;
          
          if (cs.sentiment === 'Positive') {
            locationData[mainRegion].categories[cs.category].positive++;
          }
          
          // Process for subregion
          if (!locationData[mainRegion].subregions[subRegion].categories[cs.category]) {
            locationData[mainRegion].subregions[subRegion].categories[cs.category] = { total: 0, positive: 0 };
          }
          locationData[mainRegion].subregions[subRegion].categories[cs.category].total++;
          
          if (cs.sentiment === 'Positive') {
            locationData[mainRegion].subregions[subRegion].categories[cs.category].positive++;
          }
          
          // Process for city
          if (!locationData[mainRegion].subregions[subRegion].cities[city].categories[cs.category]) {
            locationData[mainRegion].subregions[subRegion].cities[city].categories[cs.category] = { total: 0, positive: 0 };
          }
          locationData[mainRegion].subregions[subRegion].cities[city].categories[cs.category].total++;
          
          if (cs.sentiment === 'Positive') {
            locationData[mainRegion].subregions[subRegion].cities[city].categories[cs.category].positive++;
          }
        });
      });
    }
    
    // Process reviews for product data
    if (viewMode === 'product') {
      const products = {
        'Water Purifiers': ['Product RO+UV', 'Product Smart RO', 'Product Copper RO'],
        'Accessories': ['Product Filter Housing', 'Product TDS Controller'],
        'Filter Cartridges': ['Product Sediment Filter', 'Product Carbon Filter']
      };
      
      filteredReviews.forEach(review => {
        // Assign to category based on review ID (for demo)
        const categoryKey = Object.keys(productData)[review.id % Object.keys(productData).length];
        const productList = products[categoryKey];
        const productName = productList[review.id % productList.length];
        
        // Initialize product if not exists
        if (!productData[categoryKey].items[productName]) {
          productData[categoryKey].items[productName] = {
            total: 0,
            positive: 0,
            categories: {}
          };
        }
        
        // Update counts
        productData[categoryKey].total++;
        productData[categoryKey].items[productName].total++;
        
        if (review.sentiment === 'Positive') {
          productData[categoryKey].positive++;
          productData[categoryKey].items[productName].positive++;
        }
        
        // Process category sentiments
        review.categorySentiments.forEach(cs => {
          // For category level
          if (!productData[categoryKey].categories[cs.category]) {
            productData[categoryKey].categories[cs.category] = { total: 0, positive: 0 };
          }
          productData[categoryKey].categories[cs.category].total++;
          
          if (cs.sentiment === 'Positive') {
            productData[categoryKey].categories[cs.category].positive++;
          }
          
          // For product level
          if (!productData[categoryKey].items[productName].categories[cs.category]) {
            productData[categoryKey].items[productName].categories[cs.category] = { total: 0, positive: 0 };
          }
          productData[categoryKey].items[productName].categories[cs.category].total++;
          
          if (cs.sentiment === 'Positive') {
            productData[categoryKey].items[productName].categories[cs.category].positive++;
          }
        });
      });
    }
    
    // Process reviews for app data
    if (viewMode === 'app') {
      const versions = ['v2.1', 'v2.0', 'v1.9'];
      
      filteredReviews.forEach(review => {
        const platform = review.source;
        const version = versions[review.id % versions.length];
        
        // Skip if not a valid platform
        if (!appData[platform]) return;
        
        // Initialize version if not exists
        if (!appData[platform].versions[version]) {
          appData[platform].versions[version] = {
            total: 0,
            positive: 0,
            categories: {}
          };
        }
        
        // Update counts
        appData[platform].total++;
        appData[platform].versions[version].total++;
        
        if (review.sentiment === 'Positive') {
          appData[platform].positive++;
          appData[platform].versions[version].positive++;
        }
        
        // Process category sentiments
        review.categorySentiments.forEach(cs => {
          // For platform level
          if (!appData[platform].categories[cs.category]) {
            appData[platform].categories[cs.category] = { total: 0, positive: 0 };
          }
          appData[platform].categories[cs.category].total++;
          
          if (cs.sentiment === 'Positive') {
            appData[platform].categories[cs.category].positive++;
          }
          
          // For version level
          if (!appData[platform].versions[version].categories[cs.category]) {
            appData[platform].versions[version].categories[cs.category] = { total: 0, positive: 0 };
          }
          appData[platform].versions[version].categories[cs.category].total++;
          
          if (cs.sentiment === 'Positive') {
            appData[platform].versions[version].categories[cs.category].positive++;
          }
        });
      });
    }
    
    return { locationData, productData, appData };
  }, [filteredReviews, viewMode]);
  
  // Toggle expanded state
  const toggleExpand = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // Check if item is expanded
  const isExpanded = (id) => !!expandedItems[id];
  
  // Calculate percentage
  const getPercentage = (positive, total) => {
    return total > 0 ? Math.round((positive / total) * 100) : 0;
  };
  
  // Get color class based on percentage
  const getColorClass = (percentage) => {
    if (percentage >= 70) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };
  
  // Get background color class
  const getBgColorClass = (percentage) => {
    if (percentage >= 70) return 'bg-green-100';
    if (percentage >= 50) return 'bg-yellow-100';
    return 'bg-red-100';
  };
  
  // Get category percentage
  const getCategoryStats = (item, category) => {
    if (!item.categories[category] || item.categories[category].total === 0) {
      return { percentage: 0, total: 0 };
    }
    
    return {
      percentage: getPercentage(item.categories[category].positive, item.categories[category].total),
      total: item.categories[category].total
    };
  };
  
  // Get row background class
  const getRowClass = (level) => {
    switch (level) {
      case 'main': return 'hover:bg-blue-50';
      case 'sub': return 'bg-gray-50 hover:bg-blue-50';
      case 'detail': return 'bg-gray-100 hover:bg-blue-50';
      default: return '';
    }
  };
  
  // Get chevron icon
  const getChevronIcon = (expanded) => {
    return expanded ? (
      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    ) : (
      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
      </svg>
    );
  };
  
  return (
    <div>
      {/* View mode tabs */}
      <div className="flex border-b border-gray-200 mb-4">
        <button
          className={`mr-8 py-2 px-1 text-sm font-medium ${
            viewMode === 'location'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setViewMode('location')}
        >
          Locations
        </button>
        <button
          className={`mr-8 py-2 px-1 text-sm font-medium ${
            viewMode === 'product'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setViewMode('product')}
        >
          Products
        </button>
        <button
          className={`py-2 px-1 text-sm font-medium ${
            viewMode === 'app'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setViewMode('app')}
        >
          Apps
        </button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                {viewMode === 'location' ? 'Region / Location' : 
                 viewMode === 'product' ? 'Category / Product' : 'Platform / Version'}
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sentiment
              </th>
              {categories[viewMode].map(category => (
                <th key={category} scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {category}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Location view */}
            {viewMode === 'location' && Object.entries(hierarchicalData.locationData).map(([region, regionData]) => (
              <React.Fragment key={`region-${region}`}>
                {/* Region row */}
                <tr className={`cursor-pointer ${getRowClass('main')}`} onClick={() => toggleExpand(`region-${region}`)}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                    <span className="mr-2">{getChevronIcon(isExpanded(`region-${region}`))}</span>
                    <span className="font-medium">{region}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                    {regionData.total}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${getColorClass(getPercentage(regionData.positive, regionData.total))}`}>
                    {getPercentage(regionData.positive, regionData.total)}% positive
                  </td>
                  {categories.location.map(category => {
                    const stats = getCategoryStats(regionData, category);
                    return (
                      <td key={category} className={`px-4 py-3 whitespace-nowrap text-sm ${
                        stats.total > 0 ? getBgColorClass(stats.percentage) : ''
                      }`}>
                        {stats.total > 0 ? (
                          <>
                            <span className={getColorClass(stats.percentage)}>{stats.percentage}%</span>
                            <span className="text-xs text-gray-500 ml-1">({stats.total})</span>
                          </>
                        ) : '-'}
                      </td>
                    );
                  })}
                </tr>
                
                {/* Subregion rows */}
                {isExpanded(`region-${region}`) && Object.entries(regionData.subregions).map(([subRegion, subRegionData]) => (
                  <React.Fragment key={`subregion-${region}-${subRegion}`}>
                    {/* Subregion row */}
                    <tr className={`cursor-pointer ${getRowClass('sub')}`} onClick={() => toggleExpand(`subregion-${region}-${subRegion}`)}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                        <div className="w-6"></div>
                        <span className="mr-2">{getChevronIcon(isExpanded(`subregion-${region}-${subRegion}`))}</span>
                        <span>{subRegion}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                        {subRegionData.total}
                      </td>
                      <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${getColorClass(getPercentage(subRegionData.positive, subRegionData.total))}`}>
                        {getPercentage(subRegionData.positive, subRegionData.total)}% positive
                      </td>
                      {categories.location.map(category => {
                        const stats = getCategoryStats(subRegionData, category);
                        return (
                          <td key={category} className={`px-4 py-3 whitespace-nowrap text-sm ${
                            stats.total > 0 ? getBgColorClass(stats.percentage) : ''
                          }`}>
                            {stats.total > 0 ? (
                              <>
                                <span className={getColorClass(stats.percentage)}>{stats.percentage}%</span>
                                <span className="text-xs text-gray-500 ml-1">({stats.total})</span>
                              </>
                            ) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                    
                    {/* City rows */}
                    {isExpanded(`subregion-${region}-${subRegion}`) && Object.entries(subRegionData.cities).map(([city, cityData]) => (
                      <tr key={`city-${region}-${subRegion}-${city}`} className={getRowClass('detail')}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                          <div className="w-12"></div>
                          <span>{city}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                          {cityData.total}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${getColorClass(getPercentage(cityData.positive, cityData.total))}`}>
                          {getPercentage(cityData.positive, cityData.total)}% positive
                        </td>
                        {categories.location.map(category => {
                          const stats = getCategoryStats(cityData, category);
                          return (
                            <td key={category} className={`px-4 py-3 whitespace-nowrap text-sm ${
                              stats.total > 0 ? getBgColorClass(stats.percentage) : ''
                            }`}>
                              {stats.total > 0 ? (
                                <>
                                  <span className={getColorClass(stats.percentage)}>{stats.percentage}%</span>
                                  <span className="text-xs text-gray-500 ml-1">({stats.total})</span>
                                </>
                              ) : '-'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </React.Fragment>
            ))}
            
            {/* Product view */}
            {viewMode === 'product' && Object.entries(hierarchicalData.productData).map(([category, categoryData]) => (
              <React.Fragment key={`category-${category}`}>
                {/* Category row */}
                <tr className={`cursor-pointer ${getRowClass('main')}`} onClick={() => toggleExpand(`category-${category}`)}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                    <span className="mr-2">{getChevronIcon(isExpanded(`category-${category}`))}</span>
                    <span className="font-medium">{category}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                    {categoryData.total}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${getColorClass(getPercentage(categoryData.positive, categoryData.total))}`}>
                    {getPercentage(categoryData.positive, categoryData.total)}% positive
                  </td>
                  {categories.product.map(cat => {
                    const stats = getCategoryStats(categoryData, cat);
                    return (
                      <td key={cat} className={`px-4 py-3 whitespace-nowrap text-sm ${
                        stats.total > 0 ? getBgColorClass(stats.percentage) : ''
                      }`}>
                        {stats.total > 0 ? (
                          <>
                            <span className={getColorClass(stats.percentage)}>{stats.percentage}%</span>
                            <span className="text-xs text-gray-500 ml-1">({stats.total})</span>
                          </>
                        ) : '-'}
                      </td>
                    );
                  })}
                </tr>
                
                {/* Product rows */}
                {isExpanded(`category-${category}`) && Object.entries(categoryData.items).map(([product, productData]) => (
                  <tr key={`product-${category}-${product}`} className={getRowClass('detail')}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                      <div className="w-6"></div>
                      <span>{product}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                      {productData.total}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${getColorClass(getPercentage(productData.positive, productData.total))}`}>
                      {getPercentage(productData.positive, productData.total)}% positive
                    </td>
                    {categories.product.map(cat => {
                      const stats = getCategoryStats(productData, cat);
                      return (
                        <td key={cat} className={`px-4 py-3 whitespace-nowrap text-sm ${
                          stats.total > 0 ? getBgColorClass(stats.percentage) : ''
                        }`}>
                          {stats.total > 0 ? (
                            <>
                              <span className={getColorClass(stats.percentage)}>{stats.percentage}%</span>
                              <span className="text-xs text-gray-500 ml-1">({stats.total})</span>
                            </>
                          ) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
            
            {/* App view */}
            {viewMode === 'app' && Object.entries(hierarchicalData.appData).map(([platform, platformData]) => (
              <React.Fragment key={`platform-${platform}`}>
                {/* Platform row */}
                <tr className={`cursor-pointer ${getRowClass('main')}`} onClick={() => toggleExpand(`platform-${platform}`)}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                    <span className="mr-2">{getChevronIcon(isExpanded(`platform-${platform}`))}</span>
                    <span className="font-medium">{platform}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                    {platformData.total}
                  </td>
                  <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${getColorClass(getPercentage(platformData.positive, platformData.total))}`}>
                    {getPercentage(platformData.positive, platformData.total)}% positive
                  </td>
                  {categories.app.map(cat => {
                    const stats = getCategoryStats(platformData, cat);
                    return (
                      <td key={cat} className={`px-4 py-3 whitespace-nowrap text-sm ${
                        stats.total > 0 ? getBgColorClass(stats.percentage) : ''
                      }`}>
                        {stats.total > 0 ? (
                          <>
                            <span className={getColorClass(stats.percentage)}>{stats.percentage}%</span>
                            <span className="text-xs text-gray-500 ml-1">({stats.total})</span>
                          </>
                        ) : '-'}
                      </td>
                    );
                  })}
                </tr>
                
                {/* Version rows */}
                {isExpanded(`platform-${platform}`) && Object.entries(platformData.versions).map(([version, versionData]) => (
                  <tr key={`version-${platform}-${version}`} className={getRowClass('detail')}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 flex items-center">
                      <div className="w-6"></div>
                      <span>{version}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 font-medium">
                      {versionData.total}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm font-medium ${getColorClass(getPercentage(versionData.positive, versionData.total))}`}>
                      {getPercentage(versionData.positive, versionData.total)}% positive
                    </td>
                    {categories.app.map(cat => {
                      const stats = getCategoryStats(versionData, cat);
                      return (
                        <td key={cat} className={`px-4 py-3 whitespace-nowrap text-sm ${
                          stats.total > 0 ? getBgColorClass(stats.percentage) : ''
                        }`}>
                          {stats.total > 0 ? (
                            <>
                              <span className={getColorClass(stats.percentage)}>{stats.percentage}%</span>
                              <span className="text-xs text-gray-500 ml-1">({stats.total})</span>
                            </>
                          ) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
            
            {/* No data message */}
            {((viewMode === 'location' && Object.keys(hierarchicalData.locationData).length === 0) || 
              (viewMode === 'product' && Object.values(hierarchicalData.productData).every(c => c.total === 0)) ||
              (viewMode === 'app' && Object.values(hierarchicalData.appData).every(p => p.total === 0))) && (
              <tr>
                <td colSpan={categories[viewMode].length + 3} className="px-4 py-8 text-center text-sm text-gray-500">
                  <div className="flex flex-col items-center">
                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="mb-2">No {viewMode} data available with current filters</p>
                    <div className="flex gap-2">
                      {viewMode !== 'location' && (
                        <button 
                          onClick={() => setViewMode('location')}
                          className="px-3 py-1 text-xs border border-blue-300 rounded-md bg-blue-50 text-blue-700"
                        >
                          View Locations
                        </button>
                      )}
                      {viewMode !== 'product' && (
                        <button 
                          onClick={() => setViewMode('product')}
                          className="px-3 py-1 text-xs border border-blue-300 rounded-md bg-blue-50 text-blue-700"
                        >
                          View Products
                        </button>
                      )}
                      {viewMode !== 'app' && (
                        <button 
                          onClick={() => setViewMode('app')}
                          className="px-3 py-1 text-xs border border-blue-300 rounded-md bg-blue-50 text-blue-700"
                        >
                          View Apps
                        </button>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Legend for hierarchy levels and color coding */}
      <div className="mt-4 flex flex-wrap gap-4 justify-end text-xs text-gray-500">
        <div className="flex items-center">
          <div className="w-3 h-3 bg-white border border-gray-300 mr-1"></div>
          <span>{viewMode === 'location' ? 'Region' : viewMode === 'product' ? 'Category' : 'Platform'}</span>
        </div>
        {viewMode === 'location' && (
          <div className="flex items-center">
            <div className="w-3 h-3 bg-gray-50 border border-gray-300 mr-1"></div>
            <span>Sub-Region</span>
          </div>
        )}
        <div className="flex items-center">
          <div className="w-3 h-3 bg-gray-100 border border-gray-300 mr-1"></div>
          <span>{viewMode === 'location' ? 'City' : viewMode === 'product' ? 'Product' : 'Version'}</span>
        </div>
        
        <div className="ml-6 flex items-center">
          <div className="w-3 h-3 bg-green-100 border border-green-300 mr-1"></div>
          <span>≥70% Positive</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 mr-1"></div>
          <span>50-69% Positive</span>
        </div>
        <div className="flex items-center">
          <div className="w-3 h-3 bg-red-100 border border-red-300 mr-1"></div>
          <span>&lt;50% Positive</span>
        </div>
      </div>
    </div>
  );
};

export default CategoryStatsMatrix;